import {
  action,
  computed,
  observable,
  toJS,
} from 'mobx';
import startCase from 'lodash/startCase';
import {
  Configuration,
  MaterialDisplay,
  Measurement,
  MixTrim,
  PlantSettings,
  PrintSettings,
  PumpAccumulatorDisplayType,
  RunMode,
  TicketSettings,
  ToleranceMode,
  WaterAdjustBy,
  WaterUnit,
} from '../constants/Configuration';
import { WebsocketEvent } from '../constants/WebsocketEvent';
import logger from '../utils/logger';
import storage from '../utils/local-storage';
import { checkNotificationModel } from '../constants/Notification';
import { toModel } from '../utils/json-mapper/toModel';
import RootStore from './root-store';
import { isFeatureEnabled } from '../utils/feature-flags';

const API_VERSION = process.env.REACT_APP_API_VERSION;
const FEATURE_PLANT_SETTINGS = isFeatureEnabled('plant_settings');

export default class SettingsStore {
  @observable configuration: typeof Configuration | null = null;
  @observable plantSettings: typeof PlantSettings | null = null;
  @observable printSettings: typeof PrintSettings | null = null;
  @observable ticketSettings: typeof TicketSettings | null = null;
  @observable apiVersion: string | null = null;
  @observable notificationSettings: any = null;
  @observable versionLastCheck: number = Date.now();
  @observable isApiVersionIncompatible: boolean = false;
  @observable ticketNumberSequence: number = 1;

  private readonly rootStore: RootStore;
  private ws: any;
  private wsTimer: number | undefined;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
  }

  @computed
  get api() {
    return this.rootStore.newAuthStore;
  }

  @computed
  get plantId(): number | null {
    return this.rootStore.locationsStore.plantId;
  }

  @computed
  get measurement(): Array<{ value: string; name: string }> {
    return Object.keys(Measurement).map((v) => ({
      value: Measurement[v as keyof typeof Measurement],
      name: startCase(v),
    }));
  }

  @computed
  get waterUnit(): Array<{ value: string; name: string }> {
    return Object.keys(WaterUnit).map((v) => ({
      value: WaterUnit[v as keyof typeof WaterUnit],
      name: v,
    }));
  }

  @computed
  get mixTrim(): Array<{ value: string; name: string }> {
    return Object.keys(MixTrim).map((v) => ({
      value: MixTrim[v as keyof typeof MixTrim],
      name: startCase(v),
    }));
  }

  @computed
  get runMode(): Array<{ value: string; name: string }> {
    return Object.keys(RunMode).map((v) => ({
      value: RunMode[v as keyof typeof RunMode],
      name: startCase(v),
    }));
  }

  @computed
  get toleranceMode(): Array<{ value: number; name: string }> {
    return Object.keys(ToleranceMode).map((v) => ({
      value: ToleranceMode[v as keyof typeof ToleranceMode],
      name: startCase(v),
    }));
  }

  @computed
  get waterAdjustBy(): Array<{ value: number; name: string }> {
    return Object.keys(WaterAdjustBy).map((v) => ({
      value: WaterAdjustBy[v as keyof typeof WaterAdjustBy],
      name: startCase(v),
    }));
  }

  @computed
  get materialDisplay(): Array<{ value: string; name: string }> {
    return Object.keys(MaterialDisplay).map((v) => ({
      value: MaterialDisplay[v as keyof typeof MaterialDisplay],
      name: startCase(v),
    }));
  }

  @computed
  get pumpAccumulatorDisplayType(): Array<{ value: string; name: string }> {
    return Object.keys(PumpAccumulatorDisplayType).map((v) => ({
      value: PumpAccumulatorDisplayType[v as keyof typeof PumpAccumulatorDisplayType],
      name: startCase(v),
    }));
  }

  @computed
  get slumpTables(): Array<{ value: string | number; name: string }> {
    return this.rootStore.slumpTableStore.slumpTables.map((v: any) => ({
      value: v.id,
      name: `${v.code} : ${v.description}`,
    }));
  }

  @computed
  get sequences(): any[] {
    return this.rootStore.dischargeStore.sequences;
  }

  @computed
  get ticketTemplates(): Array<{ value: string | number; name: string }> {
    return this.rootStore.ticketStore.templates.map((v: any) => ({
      value: v.id,
      name: v.description,
    }));
  }

  @computed
  get printers(): Array<{ value: string; name: string }> {
    return this.rootStore.pacStore.printers.map((v: any) => ({
      value: v.deviceId,
      name: v.name,
    }));
  }

  @action
  setSettings(data: any): void {
    this.configuration = toModel(Configuration, data) as typeof Configuration;
    this.plantSettings = toModel(PlantSettings, data) as typeof PlantSettings;
    this.printSettings = toModel(PrintSettings, data) as typeof PrintSettings;
    this.ticketSettings = toModel(TicketSettings, data) as typeof TicketSettings;
  }

  @action
    loadConfiguration = (): Promise<void> => {
      const { materialStore, batchStore } = this.rootStore;
      const url = FEATURE_PLANT_SETTINGS
        ? `/api/batch/plant-settings/${this.plantId}`
        : `/api/plant/${this.plantId}/configuration`;

      return this.api
        .makeAuthorizedRequest(url)
        .then((data: any) => {
          this.setSettings(data);
          storage.setItem('waterWeightVolume', this.configuration?.waterWeightVolume);
          materialStore.setBatchMode();
        })
        .catch((e: any) => {
          logger.error(e);
          batchStore.showError('Couldn\'t load configuration, fetching from offline');
          this.api.storeDB.init()
            .then(() => this.api.storeDB.getByKey(url))
            .then((data: any) => {
              this.setSettings(data);
              materialStore.setBatchMode();
            });
        });
    };

  @action
    saveConfiguration = (data: any): Promise<any> => {
      const url = FEATURE_PLANT_SETTINGS
        ? `/api/batch/plant-settings/${this.plantId}`
        : `/api/plant/${this.plantId}/configuration`;

      return this.api
        .makeAuthorizedRequest(url, data, 'PUT')
        .then((resp: any) => {
          data = resp || { ...data };
          this.setSettings(data);
          this.api.storeDB.init().then(() => this.api.storeDB.save(url, toJS(data)));
          storage.setItem('waterWeightVolume', this.configuration?.waterWeightVolume);

          const { weatherStore, materialStore } = this.rootStore;
          weatherStore.getCurrentWeather();
          materialStore.setBatchMode();
          return resp;
        });
    };

  @action
    getApiVersion = (): Promise<void> => {
      return this.api
        .makeAuthorizedRequest('/api/version')
        .then((resp: any) => {
          this.apiVersion = resp ? resp.version : null;
        });
    };

  loadNotificationSettings(): Promise<any> {
    const url = `/api/plant/${this.plantId}/configuration/notification`;
    return this.api
      .makeAuthorizedRequest(url)
      .then((data: any) => (this.notificationSettings = checkNotificationModel(data)))
      .catch((e: any) => {
        logger.error(e);
        this.rootStore.batchStore.showError('Couldn\'t load notification settings, fetching from offline');
        this.api.storeDB.init()
          .then(() => this.api.storeDB.getByKey(url))
          .then((data: any) => (this.notificationSettings = checkNotificationModel(data)));
      });
  }

  saveNotificationSettings(data: any): Promise<any> {
    return this.api
      .makeAuthorizedRequest(`/api/plant/${this.plantId}/configuration/notification`, data, 'POST')
      .then((data: any) => (this.notificationSettings = checkNotificationModel(data)));
  }

  async saveTicketNumberSequence(start: number): Promise<any> {
    return this.api
      .makeAuthorizedRequest(`/api/batch/ticket-number-sequence?plantId=${this.plantId}`, { start }, 'PUT');
  }

  async getTicketNumberSequence(): Promise<any> {
    return this.api
      .makeAuthorizedRequest(`/api/batch/ticket-number-sequence?plantId=${this.plantId}`);
  }

  @action
  hasApiVersionChanged(): boolean {
    logger.log('api:', this.apiVersion, API_VERSION);

    if (!this.apiVersion || !API_VERSION) {
      return false;
    }

    const apiParts = this.apiVersion.split('.');
    const desiredApiVersionParts = API_VERSION.split('.');

    return apiParts.length < 3 ||
      desiredApiVersionParts.length < 2 ||
      Number(apiParts[0]) !== Number(desiredApiVersionParts[0]) ||
      Number(apiParts[1]) !== Number(desiredApiVersionParts[1]);
  }

  checkAPIVersion(): Promise<void> {
    const now = Date.now();

    if (this.apiVersion && (now - this.versionLastCheck) / 1000 < 60) {
      return Promise.resolve();
    }

    this.versionLastCheck = now;

    return this.getApiVersion().then(() => {
      this.isApiVersionIncompatible = this.hasApiVersionChanged();
    });
  }

  startWebSockets = (): void => {
    const { notifierStore, websocketStore, connectionStore } = this.rootStore;
    this.ws = websocketStore.connection;
    clearTimeout(this.wsTimer);
    this.checkAPIVersion().catch((e: any) => logger.warn(e.message));

    if (!this.ws || !connectionStore.isOnline || !websocketStore.isTrulyConnected) {
      this.wsTimer = window.setTimeout(() => {
        this.startWebSockets();
      }, 5000);
      return;
    }

    this.ws.on(WebsocketEvent.CONFIGURATION_UPDATE, (resp: any) => {
      logger.log('ws:', WebsocketEvent.CONFIGURATION_UPDATE, resp);

      if (resp) {
        this.configuration = resp;
        notifierStore.showInfo('Settings updated successfully!');
      }
    });

    this.ws.onclose(this.startWebSockets);
  };
}
